import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { SEONavbar, SEOFooter } from '../components/SEOLayout';
import PublicChatWidget from '../components/PublicChatWidget';
import { getPostBySlug, getAllPosts } from '../data/blogPosts';

// Per-artikel-innehåll. Hålls separat för att posts-data-modulen ska vara lätt.
const ARTICLES = {
  'basta-bygg-kalkyl-program-2026': () => (
    <>
      <p className="text-lg leading-relaxed text-[#3C3835]">
        Att välja rätt <strong>bygg kalkyl­program</strong> är en av de viktigaste investeringarna du gör som
        byggentreprenör. Skillnaden mellan ett bra och ett dåligt verktyg kan vara skillnaden mellan att vinna
        eller förlora upphandlingen. Här jämför vi sex av de mest populära verktygen på svenska marknaden 2026.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Vad utvärderar vi?</h2>
      <ul className="space-y-2 pl-5 list-disc text-[#3C3835]">
        <li><strong>AI-stöd</strong> — kan programmet läsa ritningar, generera materiallista automatiskt?</li>
        <li><strong>Hastighet</strong> — hur lång tid tar en typisk offert?</li>
        <li><strong>Pris</strong> — månadskostnad och eventuella tilläggsavgifter.</li>
        <li><strong>Integrationer</strong> — Fortnox, Bokio, lönesystem.</li>
        <li><strong>Svensk anpassning</strong> — ROT-avdrag, BAS-kontoplan, svenska kollektivavtal.</li>
      </ul>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>1. Operio — vinnare 2026</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Operio är den enda svenska SaaS:en som kombinerar <strong>AI-driven kalkyl</strong> med GPS-tidrapportering,
        AI-bokföring och BankID-signering i ett system. Du laddar upp en ritning, anger arbetstyp, och får en
        komplett materiallista med priser inom 30 sekunder.
      </p>
      <p className="leading-relaxed text-[#3C3835] mt-3">
        <strong>Pris:</strong> Från 1 990 kr/mån. <strong>AI:</strong> Ja, GPT-5 + ritnings­analys.
        <strong> Integrationer:</strong> Fortnox/Bokio (Q2 2026), Stripe, Resend.
        <strong> Bäst för:</strong> Bygg-, mark- och VVS-företag med 1–50 anställda som vill skala lönsamt.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>2. Excel + Word-mallar</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Det klassiska valet — och det vi gärna ersätter. Excel är kraftfullt men varje offert tar 4–8 timmar att räkna
        manuellt, och felmarginalen är hög. Funkar fortfarande för enmansföretag med få offerter, men begränsar tillväxt.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>3. Wikells Sektionsfakta</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Klassisk svensk lösning med omfattande prisbank. Stark på detaljkalkyl, svag på automatisering — kräver att
        du manuellt sätter in alla mängder. Pris från ~3 500 kr/mån.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>4. Bidcon / RIB</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Norsk-svensk lösning som riktar sig till större entreprenörer. Kraftfullt men komplext — kräver utbildning och
        är dyrt för mindre bolag (från ~5 000 kr/mån + onboarding­avgift).
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>5. Buildsafe Kalkyl</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Säkerhets­fokuserat verktyg som även gör kalkyl. Bra om du redan kör Buildsafe — annars rätt nischat.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>6. Egenutvecklat / hemmasnickrat</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Ofta sett hos byggentreprenörer som byggde ett Google-Sheet-system 2018 och fortfarande använder det. Funkar
        tills personen som byggde det slutar. Inte rekommenderat 2026.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Sammanfattning</h2>
      <p className="leading-relaxed text-[#3C3835]">
        För 95% av svenska bygg-/markentreprenörer är <strong>Operio</strong> det bästa valet 2026 — bäst AI, lägst inträdes­tröskel
        och svensk-anpassat. Wikells och Bidcon är värda att kolla på om du är ett stort företag med befintliga
        prisbanker. Allt annat kommer att försvinna inom 3 år.
      </p>

      <div className="bg-[#1A1A1A] rounded-2xl p-7 mt-10 not-prose">
        <p className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Testa Operio gratis i 14 dagar</p>
        <p className="text-sm text-white/70 mt-1">Inga kortuppgifter. Räkna din första offert idag.</p>
        <Link to="/register" className="inline-block mt-4">
          <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full">Starta gratis <ArrowRight size={14} className="ml-1.5" /></Button>
        </Link>
      </div>
    </>
  ),
  'digitalisering-byggbranschen-guide': () => (
    <>
      <p className="text-lg leading-relaxed text-[#3C3835]">
        <strong>Digitalisering i byggbranschen</strong> är inte en framtidsfråga — det händer nu. Företag som
        digitaliserar 2026 kommer att slå sina konkurrenter med 30–50% i marginal till 2028. Här är vår
        90-dagars­plan från Excel-kaos till modern AI-driven verksamhet.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Varför digitalisera nu?</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Bygg- och anläggnings­branschen har historiskt varit en av Sveriges minst digitaliserade. Enligt Sveriges
        Byggindustrier (BI) digitaliserar nu 73% av medlemsbolagen aktivt sin verksamhet — drivet av tre saker:
      </p>
      <ul className="space-y-2 pl-5 list-disc text-[#3C3835] mt-3">
        <li><strong>Personalbrist</strong> — fler offerter och projekt med samma tjänstemän kräver automatisering.</li>
        <li><strong>Marginalpressen</strong> — manuellt arbete kostar 200–400 kr/timme i tjänstemannarbete.</li>
        <li><strong>AI som tippad tipping-point</strong> — det blev 100× billigare 2024–2026.</li>
      </ul>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>90-dagars­planen</h2>

      <h3 className="text-xl font-bold mt-6 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Dag 1–14: Stäng pappret</h3>
      <p className="leading-relaxed text-[#3C3835]">
        Inför digital tidrapportering med GPS. Inga fler pappers­tidskrivare. Det här ensam­problemet sparar typiskt
        15–25% i lönekostnader genom att eliminera bortglömda timmar och felrapporteringar. Förvänta motstånd från
        äldre montörer — kör ett 14-dagars test med en pilotgrupp först.
      </p>

      <h3 className="text-xl font-bold mt-6 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Dag 15–45: AI på kalkyl &amp; offert</h3>
      <p className="leading-relaxed text-[#3C3835]">
        Sluta räkna offerter i Excel. Använd ett <Link to="/bygg-kalkyl-program" className="text-[#E8613A] underline">AI-drivet kalkylprogram</Link>{' '}
        som läser ritningar och genererar mängdlistor. Förvänta att första 5–10 offerterna kräver justering — efter det
        lär systemet sig din typ av jobb och blir snabbare än vad du tror är möjligt.
      </p>

      <h3 className="text-xl font-bold mt-6 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Dag 46–75: Bokföring &amp; fakturering</h3>
      <p className="leading-relaxed text-[#3C3835]">
        Migrera från manuell bokföring till AI-bokföring som kan klassificera fakturor automatiskt på BAS-konton.
        Slut på lördagar med papperskvitton. Spara 8–12 timmar/månad bara på bokföring.
      </p>

      <h3 className="text-xl font-bold mt-6 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Dag 76–90: Kundkommunikation</h3>
      <p className="leading-relaxed text-[#3C3835]">
        Inför kundportal där byggherren kan se framsteg, signera ÄTA med BankID och få automatiska uppdateringar. Det
        här minskar kund­ringningar med 60–80% och ökar kundnöjdheten dramatiskt.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Vanliga fallgropar</h2>
      <ul className="space-y-2 pl-5 list-disc text-[#3C3835]">
        <li><strong>Att köpa för många system.</strong> Ett system för tid, ett för faktura, ett för projekt = sämre än Excel. Välj ett allt-i-ett.</li>
        <li><strong>Att inte utbilda personalen.</strong> 2 timmars onboarding sparar 50 timmars frustration.</li>
        <li><strong>Att inte involvera fältpersonal.</strong> Om montörerna inte gillar appen, kommer det inte att fungera.</li>
      </ul>

      <h2 className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Slutsats</h2>
      <p className="leading-relaxed text-[#3C3835]">
        Digitalisering är inte en kostnad — det är ditt största lyft­hjälpmedel för marginal. Företag som börjar idag är
        framtidens vinnare. Företag som väntar till 2028 förlorar mot konkurrenter som kan offerera snabbare och
        leverera billigare.
      </p>

      <div className="bg-[#1A1A1A] rounded-2xl p-7 mt-10 not-prose">
        <p className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Redo att börja?</p>
        <p className="text-sm text-white/70 mt-1">Operio är ett av få allt-i-ett-system för svenska byggare. Testa gratis i 14 dagar.</p>
        <Link to="/register" className="inline-block mt-4">
          <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full">Kom igång <ArrowRight size={14} className="ml-1.5" /></Button>
        </Link>
      </div>
    </>
  ),
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  if (!post) return <Navigate to="/blogg" replace />;

  const ContentRenderer = ARTICLES[slug];
  const related = getAllPosts().filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }}>
      <SEO
        title={`${post.title} | Operio`}
        description={post.excerpt}
        canonical={`/blogg/${post.slug}`}
        keywords={post.keywords}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.excerpt,
          datePublished: post.date,
          author: { '@type': 'Organization', name: 'Operio AB' },
          publisher: { '@type': 'Organization', name: 'Operio AB', logo: { '@type': 'ImageObject', url: 'https://operio.nu/operio-logo-2.png' } },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://operio.nu/blogg/${post.slug}` },
        }}
      />
      <SEONavbar activePage="blogg" />

      <main className="pt-24 sm:pt-28 pb-20">
        <article className="px-5 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <Link to="/blogg" className="inline-flex items-center gap-1.5 text-sm text-[#6B6560] hover:text-[#1A1A1A]">
              <ArrowLeft size={14} /> Tillbaka till bloggen
            </Link>

            <p className="text-xs font-bold uppercase tracking-wider text-[#E8613A] mt-8">{post.category}</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mt-3 leading-[1.1]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 text-xs text-[#7A7570]">
              <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(post.date).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span className="inline-flex items-center gap-1"><Clock size={11} /> {post.readMinutes} min läsning</span>
            </div>

            <div className="prose prose-stone max-w-none mt-8 text-[#3C3835]">
              {ContentRenderer ? <ContentRenderer /> : <p>Innehållet finns inte ännu.</p>}
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="px-5 sm:px-6 mt-20">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Läs vidare</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blogg/${r.slug}`} className="bg-white border border-[#E8E2D9] rounded-xl p-5 hover:border-[#E8613A]/40 transition-colors">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#E8613A]">{r.category}</p>
                    <p className="font-bold text-[#1A1A1A] mt-1.5">{r.title}</p>
                    <p className="text-xs text-[#6B6560] mt-1">{r.excerpt.substring(0, 110)}…</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <SEOFooter />
      <PublicChatWidget />
    </div>
  );
};

export default BlogPostPage;
